import { useCallback, useEffect, useState } from 'react';

export const PALETTE_TESTID = 'global-search-dialog';

// Any MUI Dialog other than the palette itself: while one is open the shortcut stays inert.
const OTHER_DIALOG_SELECTOR = `.MuiDialog-root:not([data-testid="${PALETTE_TESTID}"])`;

export function isMacLike() {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/** Ctrl+K on Windows/Linux, Cmd+K on macOS. `code` covers keyboard layouts where `key` is not "k". */
export function isPaletteShortcut(event) {
  if (event.altKey || event.shiftKey) return false;
  if (!(event.metaKey || event.ctrlKey)) return false;
  return event.key === 'k' || event.key === 'K' || event.code === 'KeyK';
}

/**
 * Palette open/close state plus the global shortcut listener. The listener runs in the capture
 * phase on `window`, so it fires even when focus sits in a text field or a MUI component that
 * stops propagation of keydown events.
 */
export default function useCommandPalette() {
  const [open, setOpen] = useState(false);
  // Text the palette opens with (the dashboard search widget hands over what was typed).
  // `openPalette` is also used directly as an onClick handler, so a non-string argument
  // (the click event) means "open empty".
  const [initialQuery, setInitialQuery] = useState('');
  const openPalette = useCallback((query) => {
    setInitialQuery(typeof query === 'string' ? query : '');
    setOpen(true);
  }, []);
  const closePalette = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const handler = (event) => {
      if (!isPaletteShortcut(event)) return;
      if (!open && document.querySelector(OTHER_DIALOG_SELECTOR)) return;
      // Browsers bind Ctrl/Cmd+K to their own search box; keep the key for the palette.
      event.preventDefault();
      setInitialQuery('');
      setOpen(true);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [open]);

  return { open, openPalette, closePalette, initialQuery };
}
